import { useState, useEffect } from 'react'
import AdminSidebar from "../../components/admin/AdminSidebar"
import AdminHeader from "../../components/admin/AdminHeader"
import { useFAQ, useFAQManagement, useFAQCategories } from "../../hooks/useFAQ"
import { Button } from "../../components/ui/button"
import { Plus, Edit, Trash2, Eye, Search, X, Check, XCircle, Globe } from "lucide-react"
import { showSuccessToast, showErrorToast } from "../../utils/toast"
import { useTranslation } from "../../i18n"
import FAQModal from "../../components/admin/FAQModal"
import FAQTranslationModal from "../../components/admin/FAQTranslationModal"

export default function FAQManagementPage() {
  const { tObj, language } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [isPublishedFilter, setIsPublishedFilter] = useState(null)
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  const [editingFAQ, setEditingFAQ] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [translationFAQ, setTranslationFAQ] = useState(null)
  const [isTranslationModalOpen, setIsTranslationModalOpen] = useState(false)

  const { faqs, total, totalPages, loading, error, refetch } = useFAQ({
    page,
    limit,
    search: searchTerm,
    category: selectedCategory || undefined,
    isPublished: isPublishedFilter,
    language,
    immediate: true
  })

  const { categories } = useFAQCategories()
  const { createFAQ, updateFAQ, deleteFAQ, loading: actionLoading } = useFAQManagement()

  const handleCreate = () => {
    setEditingFAQ(null)
    setIsModalOpen(true)
  }

  const handleEdit = (faq) => {
    setEditingFAQ(faq)
    setIsModalOpen(true)
  }

  const handleManageTranslations = (faq) => {
    setTranslationFAQ(faq)
    setIsTranslationModalOpen(true)
  }

  const handleTranslationModalClose = () => {
    setIsTranslationModalOpen(false)
    setTranslationFAQ(null)
  }

  const handleDelete = async (faq) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la FAQ "${faq.question}" ?`)) {
      return
    }

    const result = await deleteFAQ(faq.id)
    if (result.success) {
      showSuccessToast({ message: 'FAQ supprimée avec succès' })
      refetch()
    } else {
      showErrorToast({ message: result.error || 'Erreur lors de la suppression' })
    }
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setEditingFAQ(null)
  }

  const handleModalSuccess = () => {
    showSuccessToast({ message: editingFAQ ? 'FAQ mise à jour avec succès' : 'FAQ créée avec succès' })
    refetch()
    handleModalClose()
  }

  const handleSearch = (value) => {
    setSearchTerm(value)
    setPage(1)
  }

  const handleCategoryChange = (category) => {
    setSelectedCategory(category)
    setPage(1)
  }

  const handlePublishedFilter = (value) => {
    setIsPublishedFilter(value)
    setPage(1)
  }

  if (loading && page === 1) {
    return (
      <div className="flex h-screen">
        <AdminSidebar />
        <div className="flex-1 ml-64 p-8">
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#141835] mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement des FAQs...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 ml-64 p-8 overflow-y-auto">
        <AdminHeader title="Gestion des FAQs" />

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Rechercher une FAQ..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div className="md:w-48">
              <select
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent bg-white"
              >
                <option value="">Toutes les catégories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            {/* Published Filter */}
            <div className="md:w-48">
              <select
                value={isPublishedFilter === null ? '' : isPublishedFilter.toString()}
                onChange={(e) => handlePublishedFilter(e.target.value === '' ? null : e.target.value === 'true')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent bg-white"
              >
                <option value="">Tous les statuts</option>
                <option value="true">Publié</option>
                <option value="false">Non publié</option>
              </select>
            </div>

            {/* Create Button */}
            <Button onClick={handleCreate} className="bg-[#141835] hover:bg-[#1a1f4a] text-white">
              <Plus className="w-4 h-4 mr-2" />
              Créer une FAQ
            </Button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* FAQs Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Question
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Catégorie
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ordre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statistiques
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {faqs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      Aucune FAQ trouvée
                    </td>
                  </tr>
                ) : (
                  faqs.map((faq) => {
                    const question = faq.translation?.question || faq.question
                    return (
                      <tr key={faq.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 max-w-md truncate">
                            {question}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                            {faq.category || 'Général'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {faq.order || 0}
                        </td>
                        <td className="px-6 py-4">
                          {faq.isPublished ? (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                              <Check className="w-3 h-3 mr-1" />
                              Publié
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
                              <XCircle className="w-3 h-3 mr-1" />
                              Non publié
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <Eye className="w-4 h-4" />
                              {faq.viewsCount || 0}
                            </span>
                            <span className="text-green-600">
                              👍 {faq.helpfulCount || 0}
                            </span>
                            <span className="text-red-600">
                              👎 {faq.notHelpfulCount || 0}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleManageTranslations(faq)}
                              disabled={actionLoading}
                              title="Gérer les traductions"
                            >
                              <Globe className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(faq)}
                              disabled={actionLoading}
                              title="Modifier"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(faq)}
                              disabled={actionLoading}
                              className="text-red-600 hover:text-red-800 hover:bg-red-50"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {page} sur {totalPages} ({total} FAQs au total)
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1 || loading}
                >
                  Précédent
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages || loading}
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* FAQ Modal */}
        {isModalOpen && (
          <FAQModal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            onSuccess={handleModalSuccess}
            faq={editingFAQ}
            categories={categories}
          />
        )}

        {/* Translation Modal */}
        {isTranslationModalOpen && translationFAQ && (
          <FAQTranslationModal
            isOpen={isTranslationModalOpen}
            onClose={handleTranslationModalClose}
            faq={translationFAQ}
            categories={categories}
          />
        )}
      </div>
    </div>
  )
}
